import { ClipboardIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

  // Reusable CodeBlock component
  const CodeBlock = ({ code }) => {
    const [copied, setCopied] = React.useState(false);
  
    const copyToClipboard = () => {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
  
    return (
      <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={copyToClipboard}
            className="p-1.5 rounded text-white"
            title="Copy to clipboard"
          >
            {copied ? (
              <span className="text-green-400 text-lg leading-none">✔</span>
            ) : (
              <ClipboardIcon className="w-4 h-4" />
            )}
          </button>
        </div>
  
        <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
          {code}
        </pre>
      </div>
    );
  };

export function renderAiopsDeploy () {
    return (

      <main className="flex-1 overflow-hidden scroll-smooth relative" id="main-content">
        <div className=" py-10 relative">
          {/* Background Blurs */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-netraa-orange/10 dark:bg-netraa-orange/5 rounded-full blur-[120px] pointer-events-none -z-10"></div>
          <div className="absolute top-40 right-0 w-[600px] h-[500px] bg-netraa-cyan/10 dark:bg-netraa-cyan/5 rounded-full blur-[100px] pointer-events-none -z-10"></div>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-6 font-mono">
            <span>Platform</span>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span>Netraa AIOps</span>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span className="text-primary font-bold">Deployment Setup</span>
          </div>

          {/* Section: Overview */}
<section className="mb-12">
  {/* Heading */}
  <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 leading-tight text-gray-900 dark:text-white">
    Gen AI Deployment Setup – <br />
    <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
      High-Level Architecture
    </span>
  </h1>

  {/* Card */}
  <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10 
                  bg-white/70 dark:bg-[#0B0D10]/70 backdrop-blur-xl shadow-lg 
                  transition-all duration-300 hover:shadow-cyan-500/10">

    {/* Accent Glow */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-cyan-500/10 dark:bg-cyan-400/10 blur-3xl rounded-full"></div>
    </div>

    <div className="p-7 border-l-4 border-cyan-500 dark:border-cyan-400">
      
      {/* Section Title */}
      <h3 className="text-2xl font-bold mb-4 flex items-center gap-3 
                     text-gray-900 dark:text-white">
        <span className="material-symbols-outlined text-cyan-500 dark:text-cyan-400">
          overview
        </span>
        Architecture Overview
      </h3>

      {/* Description */}
      <p className="mb-5 text-gray-600 dark:text-gray-300 leading-relaxed">
        This document outlines the deployment architecture for the Gen AI platform,
        structured into four specialized operational layers designed for scalability,
        security, and high-performance AI processing.
      </p>

      {/* List */}
      <ul className="space-y-3 text-gray-700 dark:text-gray-300">
        <li className="flex items-start gap-2">
          <span className="mt-1 h-2 w-2 rounded-full bg-cyan-500"></span>
          <span>
            <strong className="text-gray-900 dark:text-white">
              Gateway Layer (Nginx):
            </strong>{" "}
            Serves as the primary entry point, handling reverse proxying,
            secure routing, and SSL termination for backend protection.
          </span>
        </li>

        <li className="flex items-start gap-2">
          <span className="mt-1 h-2 w-2 rounded-full bg-blue-500"></span>
          <span>
            <strong className="text-gray-900 dark:text-white">
              Backend Layer:
            </strong>{" "}
            The central processing hub hosting core APIs, orchestration logic,
            AI/ML services, and predictive models.
          </span>
        </li>

        <li className="flex items-start gap-2">
          <span className="mt-1 h-2 w-2 rounded-full bg-indigo-500"></span>
          <span>
            <strong className="text-gray-900 dark:text-white">
              LLM Server:
            </strong>{" "}
            A dedicated compute environment optimized for Large Language Model
            inference and high-throughput prompt processing.
          </span>
        </li>

        <li className="flex items-start gap-2">
          <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500"></span>
          <span>
            <strong className="text-gray-900 dark:text-white">
              Database Layer (DB):
            </strong>{" "}
            A hybrid storage layer designed to handle relational data and
            vector embeddings for semantic search and RAG workflows.
          </span>
        </li>
      </ul>
    </div>
  </div>
{/* Constrained Diagram Card */}
<div className="relative mt-6 mb-12 max-w-5xl mx-auto rounded-2xl border border-gray-200 dark:border-white/10 
                bg-white/70 dark:bg-[#0B0D10]/70 backdrop-blur-xl shadow-xl 
                overflow-hidden transition-colors">

  {/* Grid Background */}
  <div className="absolute inset-0 grid-bg opacity-20 dark:opacity-10"></div>

  <div className="relative z-10 p-4 flex flex-col items-center gap-6">

    {/* Horizontal Flow - Top Row */}
    <div className="flex flex-nowrap justify-center items-start gap-3 w-full">

      {/* Frontend */}
      <div className="diagram-node w-28 bg-gray-50 dark:bg-white/5 
                      border border-gray-200 dark:border-white/10 
                      rounded-xl p-3 flex flex-col items-center shadow-md shrink-0 mt-10">
        <span className="material-symbols-outlined text-blue-500 text-2xl mb-1">
          extension
        </span>
        <span className="text-[10px] font-bold text-gray-900 dark:text-white text-center">
          Frontend Extension
        </span>
      </div>

      {/* Arrow */}
      <div className="flex items-center shrink-0 mt-16">
        <div className="w-4 h-[2px] bg-gray-400 dark:bg-white/40"></div>
        <span className="text-gray-400 dark:text-white/40 text-xs">›</span>
      </div>

      {/* Gateway */}
      <div className="diagram-node w-28 bg-cyan-50 dark:bg-cyan-500/10 
                      border border-cyan-300/40 dark:border-cyan-400/30 
                      rounded-xl p-3 flex flex-col items-center shadow-lg shrink-0 mt-10">
        <span className="material-symbols-outlined text-cyan-500 text-2xl mb-1">
          dns
        </span>
        <span className="text-[10px] font-bold text-gray-900 dark:text-white text-center">
          Gateway (Nginx)
        </span>
      </div>

      {/* Arrow */}
      <div className="flex items-center shrink-0 mt-16">
        <div className="w-4 h-[2px] bg-gray-400 dark:bg-white/40"></div>
        <span className="text-gray-400 dark:text-white/40 text-xs">›</span>
      </div>

      {/* Backend Services + LLM below */}
      <div className="flex flex-col items-center shrink-0">
        <div className="diagram-node w-28 bg-orange-50 dark:bg-orange-500/10 
                        border border-orange-300/40 dark:border-orange-400/30 
                        rounded-xl p-3 flex flex-col items-center shadow-lg">
          <span className="material-symbols-outlined text-orange-500 text-2xl mb-1">
            memory
          </span>
          <span className="text-[10px] font-bold text-gray-900 dark:text-white text-center">
            Backend Services
          </span>
          <span className="text-[8px] text-gray-600 dark:text-gray-400 mt-1 text-center">
            API • AI Services • Connectors
          </span>
        </div>

        {/* Downward Arrow to LLM */}
<div className="flex flex-col items-center my-1">
  <div className="h-4 w-[2px] bg-gray-400 dark:bg-white/40"></div>
  <span className="text-gray-400 dark:text-white/40 text-xs leading-none">⌄</span>
</div>

        {/* LLM Server */}
        <div className="diagram-node w-28 bg-purple-50 dark:bg-purple-500/10 
                        border border-purple-300/40 dark:border-purple-400/30 
                        rounded-xl p-3 flex flex-col items-center shadow-lg">
          <span className="material-symbols-outlined text-purple-500 text-2xl mb-1">
            smart_toy
          </span>
          <span className="text-[10px] font-bold text-gray-900 dark:text-white text-center">
            LLM Server
          </span>
        </div>
      </div>

      {/* Arrow */}
      <div className="flex items-center shrink-0 mt-16">
        <div className="w-4 h-[2px] bg-gray-400 dark:bg-white/40"></div>
        <span className="text-gray-400 dark:text-white/40 text-xs">›</span>
      </div>

      {/* Database */}
      <div className="diagram-node w-28 bg-emerald-50 dark:bg-emerald-500/10 
                      border border-emerald-300/40 dark:border-emerald-400/30 
                      rounded-xl p-3 flex flex-col items-center shadow-lg shrink-0 mt-10">
        <span className="material-symbols-outlined text-emerald-500 text-2xl mb-1">
          database
        </span>
        <span className="text-[10px] font-bold text-gray-900 dark:text-white text-center">
          Database
        </span>
      </div>

    </div>

  </div>
</div>
          </section>

{/* Database Layer */}
<section className="mb-16 scroll-mt-24" id="database-layer">
  {/* Section Header */}
  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
    <span className="material-symbols-outlined text-netraa-orange">storage</span>
    Database Layer
  </h2>

  {/* Database Cards */}
  <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
    {/* Postgres Card */}
    <div className="p-6 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#111217] shadow-md hover:shadow-lg transition-shadow">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Postgres with pgVector</h3>
      <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 ml-2">
        <li>
          <strong>Docker Image:</strong>{" "}
          <code className="bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">
            pgvector/pgvector:pg16
          </code>
        </li>
        <li><strong>Purpose:</strong> Store relational data and vector embeddings for AI/ML queries</li>
        <li><strong>Port:</strong> 5432</li>
        <li>
          <strong>Data Persistence:</strong>{" "}
          <code className="bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">
            ./pgdata:/var/lib/postgresql/data
          </code>
        </li>
      </ul>
    </div>

    {/* Qdrant Card */}
    <div className="p-6 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#111217] shadow-md hover:shadow-lg transition-shadow">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Qdrant Vector Database</h3>
      <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 ml-2">
        <li>
          <strong>Docker Image:</strong>{" "}
          <code className="bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">
            qdrant/qdrant:latest
          </code>
        </li>
        <li><strong>Purpose:</strong> High-performance vector database for AI operations (semantic search, embeddings)</li>
        <li><strong>Ports:</strong> 6333 (API), 6334 (optional)</li>
        <li>
          <strong>Data Persistence:</strong>{" "}
          <code className="bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">
            ./qdrant_storage:/qdrant/storage
          </code>
        </li>
        <li><strong>Timezone:</strong> Asia/Kolkata</li>
      </ul>
    </div>
  </div>

  {/* Docker Compose Code Block */}
  <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-[#1b1e22] shadow-md">
    <div className="flex items-center justify-between px-4 py-2 bg-gray-200 dark:bg-[#111217] border-b border-gray-300 dark:border-white/10">
      <span className="text-xs text-gray-700 dark:text-gray-400 font-mono">
        docker-compose.yml (Database Layer)
      </span>
    </div>

    <div className="p-4 overflow-x-auto">
      <CodeBlock
        code={`version: "3.8"
services:
  postgres:
    image: pgvector/pgvector:pg16
    container_name: postgres-pgvector
    restart: unless-stopped
    environment:
      POSTGRES_USER: postgres
      POSTGRES_PASSWORD: postgres
      POSTGRES_DB: vectordb
    ports:
      - "5432:5432"
    volumes:
      - ./pgdata:/var/lib/postgresql/data

  vector:
    image: qdrant/qdrant:latest
    container_name: aiops-vector
    restart: unless-stopped
    ports:
      - "6333:6333"
    volumes:
      - ./qdrant_storage:/qdrant/storage
    environment:
      - TZ=Asia/Kolkata
`}
      />

      {/* Commands */}
      <div className="pt-4 space-y-3">
        <p className="text-sm font-medium text-gray-900 dark:text-white">After finishing the DB docker-compose file, run:</p>
        <CodeBlock code={`docker compose up -d`} />

        <p className="text-sm font-medium text-gray-900 dark:text-white">To verify:</p>
        <CodeBlock code={`docker ps`} />
      </div>
    </div>
  </div>
</section>

{/* Backend Layer */}
<section className="mb-16 scroll-mt-24" id="backend-layer">
  {/* Section Header */}
  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
    <span className="material-symbols-outlined text-netraa-orange">memory</span>
    Backend Layer
  </h2>

  {/* Backend Table */}
  <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10 shadow-md mb-8">
    <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
      <thead className="bg-gray-50 dark:bg-[#1b1e22] text-gray-900 dark:text-white uppercase font-mono text-xs border-b border-gray-200 dark:border-white/10">
        <tr>
          <th className="px-6 py-3">Service</th>
          <th className="px-6 py-3">Container Name</th>
          <th className="px-6 py-3">Purpose</th>
          <th className="px-6 py-3">Ports</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-white/10 bg-white dark:bg-[#111217]">
        <tr className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
          <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">Main API</td>
          <td className="px-6 py-4 font-mono">aiops-server</td>
          <td className="px-6 py-4">Core backend service</td>
          <td className="px-6 py-4 font-mono">3000</td>
        </tr>
        <tr className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
          <td className="px-6 py-4 font-medium text-netraa-orange">AI Service</td>
          <td className="px-6 py-4 font-mono">aiops-app</td>
          <td className="px-6 py-4">AI/ML Operations</td>
          <td className="px-6 py-4 font-mono">8080</td>
        </tr>
        <tr className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
          <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">Predictor</td>
          <td className="px-6 py-4 font-mono">predictor-app</td>
          <td className="px-6 py-4">Forecasting / prediction service</td>
          <td className="px-6 py-4 font-mono">8000</td>
        </tr>
      </tbody>
    </table>
  </div>

  {/* Backend Docker Compose */}
  <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-[#1b1e22] shadow-md mb-6">
    <div className="flex items-center justify-between px-4 py-2 bg-gray-200 dark:bg-[#111217] border-b border-gray-300 dark:border-white/10">
      <span className="text-xs text-gray-700 dark:text-gray-400 font-mono">docker-compose.yml (Backend Layer)</span>
    </div>
    <div className="p-4 overflow-x-auto">
      <CodeBlock
        code={`services:
  server:
    image: python:latest
    container_name: aiops-server
    volumes:
      - $SERVER_PATH:/app:rw
    working_dir: /app
    command: bash -c "if [ -f ./start.sh ]; then bash ./start.sh; elif [ -f ./server.sh ]; then bash ./server.sh; else python main.py; fi"
    restart: unless-stopped
    environment:
      - TZ=Asia/Kolkata

  app:
    image: python:latest
    container_name: aiops-app
    volumes:
      - $APP_PATH:/app:rw
    working_dir: /app
    command: bash -c "if [ -f ./start.sh ]; then bash ./start.sh; elif [ -f ./app.sh ]; then bash ./app.sh; else python app.py; fi"
    restart: unless-stopped
    ports:
      - "8080:8080"
    environment:
      - TZ=Asia/Kolkata

  predictor:
    image: python:latest
    container_name: predictor-app
    volumes:
      - $PREDICTOR_APP_PATH:/app:rw
    working_dir: /app
    command: bash -c "if [ -f ./start.sh ]; then bash ./start.sh; elif [ -f ./Predictor ]; then ./Predictor; elif [ -f ./Predictor.py ]; then python ./Predictor; else echo 'No start script found in /Predictor' && tail -f /dev/null; fi"
    restart: unless-stopped
    ports:
      - "8000:8000"
    environment:
      - TZ=Asia/Kolkata
`}
      />
    </div>
  </div>
</section>

{/* Backend Environment Configuration */}
<section className="mb-16 scroll-mt-24" id="backend-env-config">
  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
    <span className="material-symbols-outlined text-netraa-orange">settings</span>
    Backend Environment Configuration
  </h2>

  <div className="bg-gray-50 dark:bg-[#111217] border border-gray-200 dark:border-white/10 rounded-xl p-6 space-y-6 shadow-md">
    <p className="text-gray-700 dark:text-gray-300">
      After completing the backend deployment, environment configuration must be added for each backend component before starting or restarting the containers.
    </p>

    {/* Backend App */}
    <div className="space-y-2">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Backend App (aiops-app)</h3>
      <p className="text-sm text-gray-700 dark:text-gray-300">Environment file location:</p>
      <CodeBlock code={`app/.env`} />
      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">This file contains:</p>
      <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
        <li>LLM and embedding model configuration</li>
        <li>Qdrant vector database connection</li>
        <li>MCP service endpoint</li>
        <li>Authentication and encryption settings</li>
      </ul>
    </div>

    {/* Backend Server */}
    {/* Backend Server */}
<div className="space-y-2">
  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Backend Server (aiops-server)</h3>
  <p className="text-sm text-gray-700 dark:text-gray-300">Environment file location:</p>
  <CodeBlock code={`server/.env`} />

  <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">This file contains:</p>
  <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
    <li>Core API configuration</li>
    <li>LLM integration details</li>
    <li>Qdrant collection and connection settings</li>
    <li>Authentication and security configuration</li>
  </ul>
</div>

{/* Predictor Service */}
<div className="space-y-2">
  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Predictor Service (predictor-app)</h3>
  <p className="text-sm text-gray-700 dark:text-gray-300">Environment setup script:</p>
  <CodeBlock code={`Predictor/env_setup.sh`} />

  <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">This script exports:</p>
  <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
    <li>Dynatrace API credentials</li>
    <li>Metric ingestion configuration</li>
    <li>Model training and forecasting parameters</li>
    <li>LLM model endpoint details</li>
  </ul>
</div>

{/* Commands */}
<div className="space-y-2">
  <p className="text-sm font-medium text-gray-900 dark:text-white">After finishing the file, run:</p>
  <CodeBlock code={`docker compose up -d`} />

  <p className="text-sm font-medium text-gray-900 dark:text-white">To verify:</p>
  <CodeBlock code={`docker ps`} />

  <p className="text-sm font-medium text-gray-900 dark:text-white">After adding or updating the environment configuration, restart the backend containers:</p>
  <CodeBlock code={`docker compose restart`} />
</div>
    {/* Note */}
    <div className="bg-blue-50 dark:bg-white/5 border border-blue-200 dark:border-white/10 rounded-lg p-4">
      <p className="text-sm text-gray-700 dark:text-gray-300">
        <span className="font-semibold text-gray-900 dark:text-white">Note:</span> Backend services connect to the LLM server to offload AI/ML processing (e.g., embeddings, large model inference). Communication occurs over HTTP, gRPC, or custom API endpoints exposed by the LLM server.
      </p>
    </div>
  </div>
</section>

{/* Gateway Layer */}
<section className="mb-16 scroll-mt-24 dark:text-white" id="gateway-layer">
  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
    <span className="material-symbols-outlined text-netraa-orange">security</span>
    Gateway Layer (Nginx Reverse Proxy)
  </h2>

  <p className="text-gray-700 dark:dark:text-white mb-4">
    The Nginx gateway serves as the single entry point for all client requests.
  </p>

  <div className="mb-6">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Purpose</h4>
    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 ml-4 space-y-1">
      <li>Terminate SSL connections.</li>
      <li>Route client requests to the correct backend service.</li>
      <li>Support WebSocket / real-time communication.</li>
      <li>Enhance security with headers and request limits.</li>
    </ul>

    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Key Features</h4>
    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 ml-4 space-y-1">
      <li>Ports: 80 (HTTP), 443 (HTTPS)</li>
      <li>
        TLS/SSL: Certificates mounted from{" "}
        <CodeBlock code={`./nginx/certs`} />
      </li>
      <li>Proxy to backend services</li>
    </ul>
  </div>

  {/* Proxy Path Table */}
  <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-border-dark mb-6 shadow-sm">
    <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
      <thead className="bg-gray-50 dark:bg-black text-gray-900 dark:text-gray-300 uppercase font-mono text-xs border-b border-gray-200 dark:border-border-dark">
        <tr>
          <th className="px-6 py-3">Path</th>
          <th className="px-6 py-3">Proxy Target</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-border-dark bg-white dark:bg-black">
        <tr className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
          <td className="px-6 py-3 font-mono">/</td>
          <td className="px-6 py-3 font-mono text-netraa-cyan">aiops-app:8000</td>
        </tr>
        <tr className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
          <td className="px-6 py-3 font-mono">/report</td>
          <td className="px-6 py-3 font-mono text-netraa-cyan">predictor-app:8000</td>
        </tr>
        <tr className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
          <td className="px-6 py-3 font-mono">/table</td>
          <td className="px-6 py-3 font-mono text-netraa-cyan">predictor-app:8000</td>
        </tr>
        <tr className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
          <td className="px-6 py-3 font-mono">/download</td>
          <td className="px-6 py-3 font-mono text-netraa-cyan">predictor-app:8000</td>
        </tr>
      </tbody>
    </table>
  </div>
{/* Docker Compose */}
<div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-[#1b1e22] shadow-md">
  {/* Header */}
  <div className="flex items-center justify-between px-4 py-2 bg-gray-200 dark:bg-[#111214] border-b border-gray-300 dark:border-white/10">
    <span className="text-xs text-gray-700 dark:text-text-secondary font-mono">
      docker-compose.yml (Gateway Layer)
    </span>
  </div>

  {/* Compose File */}
  <div className="p-4 overflow-x-auto bg-gray-50 dark:bg-black/90">
    <CodeBlock
      code={`services:
  proxy:
    image: nginx:latest
    container_name: aiops-proxy
    restart: unless-stopped
    ports:
      - "80:80"
      - "443:443"
    volumes:
      - ./nginx/nginx.conf:/etc/nginx/nginx.conf:ro
      - ./nginx/certs:/etc/nginx/certs:ro
    environment:
      - TZ=Asia/Kolkata`}
    />
  </div>

  {/* Commands */}
  <div className="pt-4 space-y-3 px-4 pb-4">
    <div>
      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
        After finishing the file, run:
      </p>
      <CodeBlock code={`docker compose up -d`} />
    </div>

    <div>
      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">To verify:</p>
      <CodeBlock code={`docker ps`} />
    </div>

    <div>
      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">View logs:</p>
      <CodeBlock code={`docker compose logs -f`} />
    </div>
  </div>
</div>
</section>

{/* LLM Server Integration */}
<section className="mb-16 scroll-mt-24" id="llm-integration">
  <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
    <span className="material-symbols-outlined text-netraa-orange">smart_toy</span>
     LLM Server Integration
  </h2>

  <ul className="list-disc list-outside text-gray-700 dark:text-gray-300 space-y-3 ml-5">
    <li><strong className="text-gray-900 dark:text-white">Role:</strong> Processes large language models, embeddings, and AI/ML inference requests.</li>
    <li><strong className="text-gray-900 dark:text-white">Connection:</strong> Backend services call LLM server endpoints for AI processing.</li>
    <li><strong className="text-gray-900 dark:text-white">Security:</strong> LLM server should be internal-only (accessible from backend containers only).</li>
    <li><strong className="text-gray-900 dark:text-white">Scaling:</strong> For high-throughput environments, the LLM layer can be horizontally scaled behind an internal load balancer.</li>
  </ul>
</section>
{/* High-Level Flow */}
<section className="mb-16 scroll-mt-24" id="high-level-flow">
  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
    <span className="material-symbols-outlined text-netraa-orange">play_arrow</span>
    High-Level Flow
  </h2>

  <ol className="relative border-l border-gray-300 dark:border-white/10 ml-4 space-y-8">
    <li className="mb-10 ml-10">
      <span className="absolute flex items-center justify-center w-8 h-8 rounded-full -left-3 ring-gray-100 dark:ring-bg-main bg-orange-500 text-white">1</span>
      <h3 className="flex items-center mb-1 text-lg font-bold text-gray-900 dark:text-white">Client Request</h3>
      <p className="mb-4 text-base font-normal text-gray-700 dark:text-gray-400">
        Client request hits the Nginx gateway at <code className="text-xs bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">https://aiops.apmosys.com</code>.
      </p>
    </li>

    <li className="mb-10 ml-10">
      <span className="absolute flex items-center justify-center w-8 h-8 rounded-full -left-3 ring-gray-100 dark:ring-bg-main bg-gray-100 dark:bg-bg-card border border-gray-300 dark:border-white/20 text-gray-700 dark:text-text-secondary">2</span>
      <h3 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">Gateway Routing</h3>
      <p className="text-base font-normal text-gray-700 dark:text-gray-400">
        The gateway routes the request to the appropriate backend container: <code className="text-xs bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">aiops-app</code>, <code className="text-xs bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">predictor-app</code>, or <code className="text-xs bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">aiops-jira-app</code>.
      </p>
    </li>

    <li className="mb-10 ml-10">
      <span className="absolute flex items-center justify-center w-8 h-8 rounded-full -left-3 ring-gray-100 dark:ring-bg-main bg-gray-100 dark:bg-bg-card border border-gray-300 dark:border-white/20 text-gray-700 dark:text-text-secondary">3</span>
      <h3 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">Backend Processing</h3>
      <ul className="list-disc list-inside text-gray-700 dark:text-gray-400 ml-4 space-y-1">
        <li>Queries Postgres or Qdrant for data/vector embeddings.</li>
        <li>Calls LLM server for AI/ML processing.</li>
      </ul>
    </li>

    <li className="mb-10 ml-10">
      <span className="absolute flex items-center justify-center w-8 h-8 rounded-full -left-3 ring-gray-100 dark:ring-bg-main bg-gray-100 dark:bg-bg-card border border-gray-300 dark:border-white/20 text-gray-700 dark:text-text-secondary">4</span>
      <h3 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">Response</h3>
      <p className="text-base font-normal text-gray-700 dark:text-gray-400">
        The response is returned back to the client via the gateway.
      </p>
    </li>
  </ol>
</section>

{/* Notes / Best Practices */}
<section className="mb-12 scroll-mt-24" id="best-practices">
  <div className="glass-card p-6 border-l-4 border-l-netraa-cyan relative overflow-hidden bg-gray-50 dark:bg-[#08090A]">
    <div className="absolute top-0 right-0 p-4 opacity-10">
      <span className="material-symbols-outlined text-6xl text-netraa-cyan">verified_user</span>
    </div>

    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
      <span className="material-symbols-outlined text-netraa-cyan">lightbulb</span>
      7. Notes / Best Practices
    </h3>

    <ul className="list-disc list-inside text-gray-700 dark:text-gray-400 space-y-2 ml-1">
  <li>Keep DB volumes persistent to avoid data loss.</li>
  <li>Set <code className="text-xs bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">restart: unless-stopped</code> for high availability.</li>
  <li>Use <code className="text-xs bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">.env</code> files for paths and credentials; avoid hardcoding.</li>
  <li>Gateway logs to stdout/stderr for container-friendly logging.</li>
  <li>Monitor backend and LLM server performance (CPU/GPU usage for LLM).</li>
  <li>Ensure all services share the same timezone (<code className="text-xs bg-gray-200 dark:bg-white/10 px-1 py-0.5 rounded text-gray-800 dark:text-white">Asia/Kolkata</code>) for consistency.</li>
</ul>
  </div>
</section>
{/* Frontend Layer (Browser Extension) */}
<section className="mb-20 scroll-mt-24" id="frontend-layer">
  <h2 className="text-2xl font-bold mb-8 flex items-center gap-2 text-gray-900 dark:text-white">
    <span className="material-symbols-outlined text-netraa-orange">devices</span>
    Frontend Layer (Browser Extension)
  </h2>

  {/* Purpose Card */}
  <div className="relative p-6 mb-6 rounded-2xl 
    border border-gray-200 dark:border-white/10 
    bg-white/80 dark:bg-[#0B0D10] 
    backdrop-blur 
    shadow-sm hover:shadow-md 
    transition-all duration-300 overflow-hidden">
    
    <div className="absolute top-0 right-0 p-4 opacity-10">
      <span className="material-symbols-outlined text-4xl text-netraa-cyan">
        smartphone
      </span>
    </div>

    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
      <span className="material-symbols-outlined text-netraa-cyan">info</span>
      Purpose
    </h3>

    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
      The frontend is implemented as a Chrome Browser Extension, which acts as the client
      interface for the Gen AI platform. It interacts securely with backend APIs via the
      Gateway (Nginx) using a custom domain.
    </p>
  </div>

  {/* Key Characteristics */}
  <div className="relative p-6 mb-6 rounded-2xl 
    border border-gray-200 dark:border-white/10 
    bg-white/80 dark:bg-[#0B0D10] 
    backdrop-blur 
    shadow-sm hover:shadow-md 
    transition-all duration-300">
    
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
      <span className="material-symbols-outlined text-netraa-cyan">settings</span>
      Key Characteristics
    </h3>

    <ul className="space-y-3 text-gray-700 dark:text-gray-300">
      <li className="flex gap-2">
        <span className="text-netraa-cyan mt-1">•</span>
        <span><strong>Deployment:</strong> Loaded locally in the client’s Chrome browser (Developer Mode)</span>
      </li>
      <li className="flex gap-2">
        <span className="text-netraa-cyan mt-1">•</span>
        <span><strong>Hosting:</strong> No standalone server required for UI</span>
      </li>
      <li className="flex gap-2">
        <span className="text-netraa-cyan mt-1">•</span>
        <span><strong>Communication:</strong> HTTPS calls to backend services via Gateway</span>
      </li>
      <li className="flex gap-2">
        <span className="text-netraa-cyan mt-1">•</span>
        <span><strong>Authentication:</strong> Token-based (JWT / API key) handled via backend</span>
      </li>
    </ul>
  </div>

  {/* Domain & Host Entry */}
  <div className="relative p-6 mb-6 rounded-2xl 
    border border-gray-200 dark:border-white/10 
    bg-white/80 dark:bg-[#0B0D10] 
    backdrop-blur 
    shadow-sm hover:shadow-md 
    transition-all duration-300">
    
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
      <span className="material-symbols-outlined text-netraa-cyan">language</span>
      Domain & Host Entry Configuration
    </h3>

    <p className="text-gray-700 dark:text-gray-300 mb-3 leading-relaxed">
      Since the frontend is a browser extension and not hosted on a public web server,
      a local or internal domain is mapped for backend access:
    </p>

    <CodeBlock code={`aiops.apmosys.com`} />

    <p className="text-gray-800 dark:text-gray-200 mt-5 mb-2 font-semibold">
      Host Entry (Client Machine)
    </p>

    <ul className="space-y-2 text-gray-700 dark:text-gray-300 ml-1 mb-4">
      <li>
        <strong>Linux / macOS:</strong>{" "}
        <code className="text-xs bg-gray-200 dark:bg-white/10 px-1.5 py-0.5 rounded text-gray-800 dark:text-gray-200 font-mono">
          sudo nano /etc/hosts
        </code>
      </li>
      <li>
        <strong>Windows:</strong>{" "}
        <code className="text-xs bg-gray-200 dark:bg-white/10 px-1.5 py-0.5 rounded text-gray-800 dark:text-gray-200 font-mono">
          C:\Windows\System32\drivers\etc\hosts
        </code>
      </li>
    </ul>

    <CodeBlock
      code={`<NGINX_GATEWAY_IP>   aiops.apmosys.com

Example:
192.168.12.95   aiops.apmosys.com`}
    />
  </div>

  {/* Chrome Extension Setup */}
  <div className="relative p-6 mb-6 rounded-2xl 
    border border-gray-200 dark:border-white/10 
    bg-white/80 dark:bg-[#0B0D10] 
    backdrop-blur 
    shadow-sm hover:shadow-md 
    transition-all duration-300">
    
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
      <span className="material-symbols-outlined text-netraa-cyan">extension</span>
      Chrome Extension Setup
    </h3>

    <ol className="space-y-2 text-gray-700 dark:text-gray-300">
      <li className="flex gap-2">
        <span className="text-netraa-cyan font-semibold">1.</span>
        <span>
          Open Chrome →{" "}
          <code className="text-xs bg-gray-200 dark:bg-white/10 px-1.5 py-0.5 rounded text-gray-800 dark:text-gray-200 font-mono">
            chrome://extensions
          </code>
        </span>
      </li>
      <li className="flex gap-2"><span className="text-netraa-cyan font-semibold">2.</span>Enable Developer mode</li>
      <li className="flex gap-2"><span className="text-netraa-cyan font-semibold">3.</span>Click Load unpacked</li>
      <li className="flex gap-2"><span className="text-netraa-cyan font-semibold">4.</span>Select the frontend extension directory</li>
      <li className="flex gap-2">
        <span className="text-netraa-cyan font-semibold">5.</span>
        <span>
          Extension will now communicate with:{" "}
          <code className="text-xs bg-gray-200 dark:bg-white/10 px-1.5 py-0.5 rounded text-gray-800 dark:text-gray-200 font-mono">
            https://aiops.apmosys.com
          </code>
        </span>
      </li>
    </ol>
  </div>

  {/* Deployment Endpoint */}
  <div className="relative p-6 rounded-2xl 
    border border-gray-200 dark:border-white/10 
    bg-gradient-to-br from-gray-50 to-white 
    dark:from-[#0B0D10] dark:to-[#08090A] 
    shadow-sm hover:shadow-md 
    transition-all duration-300">
    
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
      <span className="material-symbols-outlined text-netraa-cyan">cloud</span>
      Deployment Endpoint
    </h3>

    <p className="text-gray-700 dark:text-gray-300 mb-3">
      All platform components are accessible through the following endpoint:
    </p>

    <CodeBlock code={`https://share.apmosys.com/s/wWjM785GQPsLH4z`} />

    <p className="text-gray-800 dark:text-gray-200 mt-4 mb-2 font-medium">
      This endpoint provides access to:
    </p>

    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
      <li>• Database layer (Postgres + Qdrant)</li>
      <li>• Backend services (AI services, predictors, APIs)</li>
      <li>• Gateway layer (Nginx reverse proxy)</li>
      <li>• Frontend UI (Chrome browser extension integration)</li>
    </ul>
  </div>
</section>
        </div>
      </main>
);
}